
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log("Sending password reset for email:", email.toLowerCase());

      // Call the edge function to handle verification and reset
      const { data, error } = await supabase.functions.invoke('send-password-reset', {
        body: { email: email.toLowerCase() }
      });

      if (error) {
        console.error("Password reset error:", error);
        throw new Error(error.message || 'Failed to send reset link');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to send reset link');
      }
      
      setSubmitted(true);
      toast.success('Password reset link sent. Check your email.');
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast.error(error.message || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F6F8] to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-[#E6EEF5] shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-[#F4F6F8] rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-[#2F80ED]" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-[#1A237E]">Reset Password</CardTitle>
          <CardDescription className="text-[#2F80ED]">
            {submitted 
              ? "Check your email for a password reset link"
              : "Enter your email to receive a password reset link"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4 text-center">
              <p className="text-[#0061A8]">
                We've sent a password reset link to <strong className="text-[#1A237E]">{email}</strong>. 
                Please check your email and follow the instructions to reset your password.
              </p>
              <Button 
                variant="outline" 
                className="w-full border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED] hover:text-white transition-colors"
                onClick={() => navigate('/auth')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="border-[#E6EEF5] focus:border-[#2F80ED] focus:ring-[#2F80ED]"
                />
              </div>
              <div className="space-y-2">
                <Button
                  type="submit"
                  className="w-full bg-[#1A237E] hover:bg-[#673AB7] text-white transition-colors"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED] hover:text-white transition-colors"
                  onClick={() => navigate('/auth')}
                  disabled={loading}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Login
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
